import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { PetType } from '../types/pet';
import {
  Camera, PawPrint, Lock, Mars, Venus, MapPin, Globe, Map, Phone, AlertTriangle, Loader2
} from 'lucide-react';
import { LOCATIONS } from '../data/locations';
import { formatGenericID } from '../utils/formatters';

interface AddPetFormProps {
  userId: string;
  onPetAdded: () => void;
  initialSpecies?: string;
  shelterId?: string;
  rescueStatus?: string;
  customTitle?: string;
  hideContainer?: boolean;
}

import { SPECIES_GROUPS, SPECIES_CONFIG, INTERVIEW_CONFIG } from '../data/species';

const AddPetForm: React.FC<AddPetFormProps> = ({
  userId,
  onPetAdded,
  initialSpecies = 'Perro',
  shelterId,
  rescueStatus,
  customTitle,
  hideContainer = false
}) => {
  const [name, setName] = useState('');
  const [species, setSpecies] = useState<string>(initialSpecies);
  const [breed, setBreed] = useState('');
  const [gender, setGender] = useState('Macho');
  const [birthDate, setBirthDate] = useState('');
  const [estimatedYears, setEstimatedYears] = useState('');
  const [estimatedMonths, setEstimatedMonths] = useState('');

  // Nuevos Campos Específicos
  const [dominantColor, setDominantColor] = useState('');
  const [markings, setMarkings] = useState('');
  const [eyeColor, setEyeColor] = useState('');
  const [weight, setWeight] = useState(''); // Peso opcional
  const [height, setHeight] = useState(''); // Altura opcional

  // Ubicación y Contacto
  const [country, setCountry] = useState('Chile');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+56');
  const [rut, setRut] = useState('');

  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  // Efecto: Cuando cambia el País
  useEffect(() => {
    if (LOCATIONS[country]) {
      const regions = Object.keys(LOCATIONS[country].regions);
      setAvailableRegions(regions);
      setRegion('');
      setCity('');
      setAvailableCities([]);
      if (LOCATIONS[country].prefix) {
        setPhonePrefix(LOCATIONS[country].prefix.trim());
      }
    }
  }, [country]);

  // Efecto: Cuando cambia la Región
  useEffect(() => {
    if (country && region && LOCATIONS[country]?.regions[region]) {
      const cities = LOCATIONS[country].regions[region];
      setAvailableCities(cities);
      setCity('');
    }
  }, [country, region]);

  // State for Interview Answers
  const [foodBrand, setFoodBrand] = useState('');
  const [foodFreq, setFoodFreq] = useState('');
  const [favReward, setFavReward] = useState('');
  const [dislikedFood, setDislikedFood] = useState('');
  const [personalityTags, setPersonalityTags] = useState('');
  const [favHuman, setFavHuman] = useState('');
  const [fears, setFears] = useState('');
  const [mischief, setMischief] = useState('');
  const [likes, setLikes] = useState('');
  const [dislikes, setDislikes] = useState('');
  const [favToy, setFavToy] = useState('');
  const [energyLevel, setEnergyLevel] = useState(5);
  const [angerTriggers, setAngerTriggers] = useState('');
  const [tricks, setTricks] = useState('');

  // Determine Interview Labels based on Species Group
  const interviewLabels = React.useMemo(() => {
    const s = species || 'Perro';
    const groupIndex = SPECIES_GROUPS.findIndex(g => g.includes(s));

    if (groupIndex === 0) return INTERVIEW_CONFIG['AMIGOS PELUDOS'];
    if (groupIndex === 1) return INTERVIEW_CONFIG['PEQUEÑOS MAMÍFEROS'];
    if (groupIndex === 2) return INTERVIEW_CONFIG['AVES'];
    if (groupIndex === 3) return INTERVIEW_CONFIG['REPTILES'];
    if (groupIndex === 4) return INTERVIEW_CONFIG['GRANJA'];

    return INTERVIEW_CONFIG['DEFAULT'];
  }, [species]);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'uploading'>('idle');

  const themeBg = gender === 'Macho' ? 'bg-sky-50/50' : 'bg-rose-50/50';
  const themeBorder = gender === 'Macho' ? 'border-sky-200' : 'border-rose-200';

  // Get Current Labels based on Species
  const currentConfig = SPECIES_CONFIG[species] || SPECIES_CONFIG['Perro'];
  const labels = currentConfig.labels;

  // Filtrar lista de especies según el grupo de initialSpecies
  const currentGroupOptions = React.useMemo(() => {
    const group = SPECIES_GROUPS.find(g => g.includes(initialSpecies || 'Perro'));
    return group || Object.keys(SPECIES_CONFIG);
  }, [initialSpecies]);

  useEffect(() => {
    // Si la especie actual no está en el grupo (cambio de initialSpecies), resetear
    /* No forzamos reset aquí para permitir libertad, pero el select se renderiza con currentGroupOptions */
  }, [initialSpecies]);

  useEffect(() => {
    const availableBreeds = currentConfig.breeds || [];
    setBreed(availableBreeds[0] || '');
  }, [species]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isShelter = rescueStatus === 'shelter';
    const hasBirthDate = !!birthDate;
    const hasEstimation = !!estimatedYears || !!estimatedMonths;

    if (!name || !species) {
      alert("Por favor completa los campos principales (Nombre y Especie).");
      return;
    }

    if (!isShelter && !hasBirthDate) {
      alert("Por favor ingresa la fecha de nacimiento.");
      return;
    }

    if (isShelter && !hasBirthDate && !hasEstimation) {
      alert("Por favor ingresa la fecha de nacimiento o edad aproximada.");
      return;
    }
    if (!userId) {
      alert("Error: No se detectó ID de usuario. Por favor re-inicia sesión.");
      return;
    }
    setStatus('uploading');

    try {
      let imageUrl = '';
      if (file) {
        const res = await api.uploadImage(file);
        if (res && res.url) imageUrl = res.url;
      }

      let finalBirthDate = birthDate;
      if (rescueStatus === 'shelter' && !birthDate) {
        const years = parseInt(estimatedYears) || 0;
        const months = parseInt(estimatedMonths) || 0;
        const d = new Date();
        d.setFullYear(d.getFullYear() - years);
        d.setMonth(d.getMonth() - months);
        finalBirthDate = d.toISOString().split('T')[0];
      }

      const finalPhone = `${phonePrefix.trim()}${phone.trim()}`;

      const response = await api.addPet({
        userId, name, species, breed, gender, birthDate: finalBirthDate,
        imageUrl,
        dominant_color: dominantColor,
        markings: markings,
        weight: parseFloat(weight) || 0,
        height: parseFloat(height) || 0,
        eye_color: eyeColor,
        // Interview Data Mapping
        food_brand: foodBrand,
        food_frequency: foodFreq,
        favorite_reward: favReward,
        disliked_food: dislikedFood,
        personality_tags: personalityTags,
        favorite_human: favHuman,
        fears: fears,
        mischief: mischief,
        likes: likes,
        dislikes: dislikes,
        favorite_toy: favToy,
        energy_level: energyLevel,
        anger_triggers: angerTriggers,
        tricks: tricks,
        shelter_id: shelterId,
        rescue_status: rescueStatus,
        // New location fields
        country,
        region,
        city,
        phone: finalPhone,
        rut
      });

      if (response && (response.message === "EXITO" || response.id)) {
        onPetAdded();
      } else {
        throw new Error(response?.error || "Error en el servidor");
      }
    } catch (error: any) {
      console.error("Error:", error);
      alert('Error: ' + error.message);
      setStatus('idle');
    }
  };

  return (
    <div className={hideContainer ? "" : `bg-white rounded-[3.5rem] overflow-hidden shadow-2xl border-2 transition-colors duration-500 ${themeBorder} mb-20 w-full max-w-3xl mx-auto`}>
      <div className={`p-10 text-center relative overflow-hidden transition-colors duration-500 ${gender === 'Macho' ? 'bg-gradient-to-r from-sky-400 to-blue-500' : 'bg-gradient-to-r from-rose-400 to-pink-500'}`}>
        <div className="relative z-10">
          <h3 className="text-4xl font-black text-white uppercase italic tracking-tighter mb-1">{customTitle || 'Nuevo Amigo'}</h3>
          <p className="text-white/80 text-[10px] font-black uppercase tracking-widest leading-none flex items-center justify-center gap-2"><Lock size={10} /> Registro Seguro</p>
        </div>
        <PawPrint className="absolute -right-6 -bottom-6 text-white/20 w-40 h-40 rotate-12" />
      </div>

      <form onSubmit={handleSubmit} className="p-8 md:p-14 space-y-8">
        {/* FOTO */}
        <div className="flex flex-col items-center">
          <div className="relative group">
            <div className={`w-44 h-44 rounded-[3rem] ${themeBg} border-4 border-white shadow-xl overflow-hidden flex items-center justify-center group-hover:scale-105 transition-all`}>
              {previewUrl ? <img src={previewUrl} className="w-full h-full object-cover" /> : <PawPrint className={`w-16 h-16 ${gender === 'Macho' ? 'text-sky-200' : 'text-rose-200'}`} />}
            </div>
            <label className={`absolute -bottom-2 -right-2 ${gender === 'Macho' ? 'bg-sky-500' : 'bg-rose-500'} text-white p-3 rounded-2xl shadow-lg border-4 border-white cursor-pointer hover:scale-110 transition-transform`}>
              <Camera size={20} />
              <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
            </label>
          </div>
        </div>

        {/* DATOS BÁSICOS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-3">Especie</label>
            <select value={species} onChange={e => setSpecies(e.target.value)} className={`w-full ${themeBg} border-2 border-transparent rounded-[1.5rem] px-5 py-4 font-bold text-slate-700 outline-none focus:bg-white focus:${themeBorder} transition-all`}>
              {currentGroupOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-3">Nombre</label>
            <input required type="text" value={name} onChange={e => setName(e.target.value)} className={`w-full ${themeBg} border-2 border-transparent rounded-[1.5rem] px-5 py-4 font-bold text-slate-700 focus:bg-white focus:${themeBorder} outline-none transition-all`} placeholder="Ej: Toby" />
          </div>

          {rescueStatus === 'shelter' ? (
            <div className="md:col-span-2 bg-orange-50/50 p-6 rounded-[2rem] border-2 border-orange-100 flex flex-col gap-4">
              <label className="text-[10px] font-black uppercase tracking-widest text-orange-400 ml-3">Edad Aproximada</label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Años</label>
                  <input
                    type="number"
                    value={estimatedYears}
                    onChange={e => setEstimatedYears(e.target.value)}
                    className="w-full bg-white border-2 border-orange-200 rounded-2xl px-5 py-4 font-bold text-slate-700 outline-none focus:border-orange-400 transition-all"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Meses</label>
                  <input
                    type="number"
                    value={estimatedMonths}
                    onChange={e => setEstimatedMonths(e.target.value)}
                    className="w-full bg-white border-2 border-orange-200 rounded-2xl px-5 py-4 font-bold text-slate-700 outline-none focus:border-orange-400 transition-all"
                    placeholder="0"
                  />
                </div>
              </div>
              <p className="text-[9px] font-bold text-orange-400 uppercase italic text-center">Calcularemos una fecha de nacimiento aproximada para el historial médico.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-3">Fecha Nacimiento (Aprox)</label>
              <input required type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} className={`w-full ${themeBg} border-2 border-transparent rounded-[1.5rem] px-5 py-4 font-bold text-slate-700 focus:bg-white focus:${themeBorder} outline-none transition-all`} />
            </div>
          )}
        </div>



        {/* GÉNERO */}
        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-3 italic">Sexo</label>
          <div className="grid grid-cols-2 gap-4">
            <button type="button" onClick={() => setGender('Macho')} className={`flex items-center justify-center gap-2 py-4 rounded-[1.5rem] font-black uppercase text-xs transition-all border-2 ${gender === 'Macho' ? 'bg-sky-100 border-sky-300 text-sky-600' : 'bg-slate-50 border-transparent text-slate-400'}`}><Mars size={18} /> Macho</button>
            <button type="button" onClick={() => setGender('Hembra')} className={`flex items-center justify-center gap-2 py-4 rounded-[1.5rem] font-black uppercase text-xs transition-all border-2 ${gender === 'Hembra' ? 'bg-rose-100 border-rose-300 text-rose-600' : 'bg-slate-50 border-transparent text-slate-400'}`}><Venus size={18} /> Hembra</button>
          </div>
        </div>

        {/* DETALLES FÍSICOS (DINÁMICOS) */}
        <div className="bg-slate-50 rounded-[2.5rem] p-6 space-y-6 border border-slate-100">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">Detalles Físicos</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">{labels.color}</label>
              <input type="text" value={dominantColor} onChange={e => setDominantColor(e.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-600 text-sm focus:border-sky-300 outline-none" placeholder="Ej: Negro, Tricolor, Manchado..." />
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">{labels.markings}</label>
              <input type="text" value={markings} onChange={e => setMarkings(e.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-600 text-sm focus:border-sky-300 outline-none" placeholder="Ej: Mancha en el ojo, Cola blanca..." />
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Color de Ojos</label>
              <input type="text" value={eyeColor} onChange={e => setEyeColor(e.target.value)} className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-600 text-sm focus:border-sky-300 outline-none" placeholder="Ej: Café, Azul, Heterocromía" />
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Peso Actual (Kg) (Opcional)</label>
              <input type="text" inputMode="decimal" value={weight} onChange={e => setWeight(e.target.value.replace(/[^0-9.]/g, ''))} className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-700 text-sm focus:border-sky-300 outline-none" placeholder="0.0" />
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-bold text-slate-400 uppercase ml-3">Altura / Talla (cm) (Opcional)</label>
              <input type="text" inputMode="decimal" value={height} onChange={e => setHeight(e.target.value.replace(/[^0-9.]/g, ''))} className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-700 text-sm focus:border-sky-300 outline-none" placeholder="0" />
            </div>
          </div>
        </div>

        <button type="submit" disabled={status === 'uploading'} className={`w-full py-6 rounded-[2rem] font-black text-lg shadow-xl transition-all transform active:scale-95 ${status === 'uploading' ? 'bg-slate-200 text-slate-400' : gender === 'Macho' ? 'bg-sky-500 text-white hover:bg-sky-600' : 'bg-rose-500 text-white hover:bg-rose-600'}`}>
          {status === 'uploading' ? 'Guardando...' : 'Crear Perfil'}
        </button>
      </form>
    </div>
  );
};
export default AddPetForm;